export enum COORDINATE_TYPE {
  LATITUDE = 'latitude',
  LONGITUDE = 'longitude',
}

const MAX_DECIMAL_PLACES = 8;
const BOTH_COORDINATES_REQUIRED_MESSAGE = 'Both latitude and longitude are required';

const COORDINATE_LIMITS: Record<COORDINATE_TYPE, { min: number; max: number; label: string }> = {
  [COORDINATE_TYPE.LATITUDE]: { min: -90, max: 90, label: 'Latitude' },
  [COORDINATE_TYPE.LONGITUDE]: { min: -180, max: 180, label: 'Longitude' },
};

export const COORDINATE_FIELD_LIMITS = {
  [COORDINATE_TYPE.LATITUDE]: {
    min: COORDINATE_LIMITS[COORDINATE_TYPE.LATITUDE].min,
    max: COORDINATE_LIMITS[COORDINATE_TYPE.LATITUDE].max,
    decimalScale: MAX_DECIMAL_PLACES,
  },
  [COORDINATE_TYPE.LONGITUDE]: {
    min: COORDINATE_LIMITS[COORDINATE_TYPE.LONGITUDE].min,
    max: COORDINATE_LIMITS[COORDINATE_TYPE.LONGITUDE].max,
    decimalScale: MAX_DECIMAL_PLACES,
  },
};

export const validateCoordinate = ({
  type,
  value,
}: {
  type: COORDINATE_TYPE;
  value: string;
}): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const parsed = Number(trimmed);
  if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
    return `${COORDINATE_LIMITS[type].label} must be a valid number`;
  }

  const { min, max, label } = COORDINATE_LIMITS[type];
  if (parsed < min || parsed > max) {
    return `${label} must be between ${min} and ${max}`;
  }

  const decimalPart = trimmed.includes('.') ? trimmed.split('.')[1] : '';
  if (decimalPart.length > MAX_DECIMAL_PLACES) {
    return `${label} can have at most ${MAX_DECIMAL_PLACES} decimal places`;
  }

  return null;
};

export const validateCoordinatePair = ({
  latitude,
  longitude,
}: {
  latitude: string;
  longitude: string;
}): { latitudeError: string | null; longitudeError: string | null } => {
  const hasLatitude = !!latitude.trim();
  const hasLongitude = !!longitude.trim();

  if (hasLatitude !== hasLongitude) {
    return {
      latitudeError: hasLatitude ? null : BOTH_COORDINATES_REQUIRED_MESSAGE,
      longitudeError: hasLongitude ? null : BOTH_COORDINATES_REQUIRED_MESSAGE,
    };
  }

  return {
    latitudeError: validateCoordinate({ type: COORDINATE_TYPE.LATITUDE, value: latitude }),
    longitudeError: validateCoordinate({ type: COORDINATE_TYPE.LONGITUDE, value: longitude }),
  };
};
